import type { PlanBundle } from '../model/plan'

/**
 * Two memos, so the same drawing is derived once and projected once.
 *
 * WHAT THEY ARE FOR. Both of the expensive steps in this feature are pure
 * functions of content that usually has not changed:
 *
 *  - Deriving a `PlanBundle` walks every ring of every space and zone on a
 *    floor. Apollo's `cache-and-network` policy re-delivers the whole response
 *    on every revalidation, and a re-render delivers it again; the *content* is
 *    identical almost every time.
 *  - Projecting a bundle into a render frame walks every ring again, once per
 *    frame the user looks at, and users go back and forth between two frames.
 *
 * Keying both on content rather than on identity is what makes that work.
 * Apollo hands back a fresh object graph after a network revalidation even when
 * the bytes are the same, so an identity check (`useMemo` on `data`) re-derives
 * for nothing; a content hash does not.
 *
 * WHAT THEY ARE NOT. In-memory only. They live in module scope, they are not
 * persisted, and they die on reload — which is the correct lifetime for derived
 * geometry whose source of truth is one network fetch away.
 *
 * THEY MUST BE CLEARED ON LOGOUT AND ORG SWITCH. `clearGeoPlanCaches()` exists
 * for exactly that, and it is not optional: a `PlanBundle` is tenant data, the
 * keys are content hashes rather than tenant-scoped names, and a bundle left in
 * this map would be a previous tenant's floor plan sitting in memory inside the
 * next tenant's session. Whoever owns the auth and org-switch paths calls this.
 */

/**
 * Twelve bundles.
 *
 * Sized for navigation, not for a working set: a user moves between the floors
 * of a building and back, and twelve covers that with room to spare while
 * bounding the memory a large floor's rings can hold.
 */
const BUNDLE_CAP = 12

/**
 * Twenty-four projections.
 *
 * Twice the bundle cap, because one bundle is legitimately projected into more
 * than one frame — that is the whole point of the frame switcher — and a cap
 * equal to the bundle cap would evict the bundle you are still looking at as
 * soon as you compared two datums.
 */
const PROJECTION_CAP = 24

const bundles = new Map<string, PlanBundle>()
const projections = new Map<string, unknown>()

/**
 * Insertion-ordered `Map` as an LRU.
 *
 * A `Map` iterates in insertion order, so `keys().next()` is the oldest entry
 * and delete-then-set on a hit moves an entry to the back. That is the whole
 * algorithm; no library, no linked list.
 *
 * `has` before `get` rather than `get() !== undefined`, so a memoized value
 * that is legitimately `undefined` is a hit rather than an infinite miss.
 */
function memo<T>(
  store: Map<string, T>,
  cap: number,
  key: string,
  build: () => T
): T {
  if (store.has(key)) {
    const hit = store.get(key) as T
    store.delete(key)
    store.set(key, hit)
    return hit
  }

  const built = build()
  store.set(key, built)

  while (store.size > cap) {
    const oldest = store.keys().next()
    if (oldest.done === true) break
    store.delete(oldest.value)
  }

  return built
}

/**
 * The derived bundle for one key, built at most once.
 *
 * Key format: `${scopeKind}:${scopeId}:${rawHash}` — see `bundleCacheKey`.
 */
export function memoBundle(key: string, build: () => PlanBundle): PlanBundle {
  return memo(bundles, BUNDLE_CAP, key, build)
}

/**
 * The projection of one bundle into one frame, built at most once.
 *
 * Key format: `${bundleVersion}:${renderFrameId}` — see `projectionCacheKey`.
 *
 * Generic in the projection type because `render/project.ts` owns that shape
 * and this module has no reason to know it. The store holds `unknown` and the
 * cast out is safe for the same reason the key is safe: `bundleVersion` names
 * the content, so two calls with one key are two calls with one input.
 */
export function memoProjection<T>(key: string, build: () => T): T {
  return memo<unknown>(projections, PROJECTION_CAP, key, build) as T
}

/**
 * The bundle key, in one place.
 *
 * `rawHash` is a content hash of the RAW GraphQL responses, not of the derived
 * bundle — the derived bundle is the thing being cached, so hashing it would
 * mean doing the work in order to find out the work was unnecessary. Hashing
 * the responses instead means a `cache-and-network` revalidation that returns
 * identical bytes is a hit, and every polygon is parsed exactly once.
 *
 * `scopeKind` and `scopeId` are in the key even though the responses they
 * produced are already hashed. They cost nothing and they make an entry legible
 * in a debugger, which matters for a cache whose values are otherwise
 * indistinguishable blobs of geometry.
 */
export function bundleCacheKey(
  scopeKind: string,
  scopeId: string,
  rawHash: string
): string {
  return `${scopeKind}:${scopeId}:${rawHash}`
}

/** The projection key, in one place. */
export function projectionCacheKey(
  bundleVersion: string,
  renderFrameId: string
): string {
  return `${bundleVersion}:${renderFrameId}`
}

/**
 * Drop everything. Called on logout and on org switch.
 *
 * See the file header: this is a tenancy boundary, not a performance knob.
 */
export function clearGeoPlanCaches(): void {
  bundles.clear()
  projections.clear()
}
